import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';

export const LOGIN_TIMEOUT_MS = 20000;

/** Reject if the promise has not settled within ms (so a hung request never fails silently). */
export function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject({ timeout: true }), ms);
    p.then(v => { clearTimeout(t); resolve(v); }, e => { clearTimeout(t); reject(e); });
  });
}

/** Plain-language message for a failed social sign-in code exchange. */
export function loginErrorMessage(provider: string, err: any): string {
  if (err && err.timeout) {
    return provider + ' sign-in timed out: the server did not respond within 20 seconds. Check your connection and try again.';
  }
  if (err && err.status === 0) {
    return provider + ' sign-in failed: could not reach the server. Check your connection (or network/firewall) and try again.';
  }
  const detail = err && err.error && (err.error.error || err.error.message);
  return provider + ' sign-in failed' + (detail ? ': ' + String(detail).split('\n')[0] : '') + '. Please try again.';
}

declare const google: any;
declare const amazon: any;

import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  error: string;
  signingIn = false;
  authType: string = "Local";
  username: string;
  password: string;

  private codeClient: any;

  constructor(private authService: AuthService, private router: Router, private route: ActivatedRoute) { }

  ngOnInit() {
    const params = this.route.snapshot.queryParamMap;
    const code = params.get('code');
    const state = params.get('state');

    if (code) {
      if (state === 'google') {
        this.handleGoogleCode({ code });
      } else if (state === 'amazon') {
        this.handleAmazonCode(code);
      }
      return;
    }

    const w = window as any;
    if (w.google && w.google.accounts && w.google.accounts.oauth2) {
      this.codeClient = w.google.accounts.oauth2.initCodeClient({
        client_id: environment.GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        ux_mode: 'redirect',
        redirect_uri: environment.GOOGLE_REDIRECT_URI,
        state: 'google'
      });
    }
  }

  private handleGoogleCode(response: any) {
    if (response && response.code) {
      this.signingIn = true;
      this.error = undefined;
      withTimeout(this.authService.authenticateWithToken('Google', response.code), LOGIN_TIMEOUT_MS).then(() => {
        this.signingIn = false;
        this.error = undefined;
        if (this.authService.isAdmin()) {
          this.router.navigate(['/admin-landing']);
        } else {
          this.router.navigate(['/lessee-landing']);
        }
      }).catch((err) => {
        this.signingIn = false;
        this.error = loginErrorMessage('Google', err);
      });
    } else {
      this.error = 'Google login failed';
    }
  }

  private handleAmazonCode(code: string) {
    this.signingIn = true;
    this.error = undefined;
    withTimeout(this.authService.authenticateWithToken('Amazon', code), LOGIN_TIMEOUT_MS).then(() => {
      this.signingIn = false;
      this.error = undefined;
      if (this.authService.isAdmin()) {
        this.router.navigate(['/admin-landing']);
      } else {
        this.router.navigate(['/lessee-landing']);
      }
    }).catch((err) => {
      this.signingIn = false;
      this.error = loginErrorMessage('Amazon', err);
    });
  }

  loginAction() {
    if (!this.username || !this.password) {
      this.error = 'Please enter your email and password.';
      return;
    }
    this.error = undefined;
    this.authService.authenticate(this.authType, this.username.trim(), this.password).then(() => {
      this.error = undefined;
      if (this.authService.isAdmin()) {
        this.router.navigate(['/admin-landing']);
      } else {
        this.router.navigate(['/lessee-landing']);
      }
    }).catch((err) => {
      const msg = (err && err.error && err.error.error) || (err && err.message);
      const status = err && err.status;
      if (status === 0) {
        this.error = 'Unable to connect. Is the API server running?';
      } else if (msg) {
        this.error = msg;
      } else {
        this.error = 'Invalid email or password, please try again.';
      }
    });
  }

  loginAmazonAction() {
    if (!(window as any).amazon || !(window as any).amazon.Login) {
      this.error = 'Amazon login not available.';
      return;
    }

    (window as any).amazon.Login.authorize({
      scope: 'profile',
      response_type: 'code',
      state: 'amazon'
    }, environment.AMAZON_REDIRECT_URI);
  }

  loginGoogleAction() {
    if (!this.codeClient) {
      this.error = 'Google login not available.';
      return;
    }

    this.codeClient.requestCode();
  }

  cancelAction() {
    this.router.navigate(['/']);
  }

}
